<script setup lang="ts">
    import { inject, ref, computed, onMounted, reactive, toRaw } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import axios from 'axios';
    import ImportMembresComponent from './@components/ImportMembresComponent.vue';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import ToastNotification from '@/components/ToastNotification.vue';
    import CustomPagination from '@/components/CustomPagination.vue';
    import { formatAmount, formatDate } from '@/utils';


    const route = useRoute();
    const router = useRouter();
    const BACKEND_URL = inject('BACKEND_URL');
    const collectivite = ref<any>(route.meta.item || {});
    const refreshMembers = ref(0);
    const usersCount = ref(0);
    const membersList = ref<any[]>([]);
    const prospectsList = ref<any[]>([]);
    const loading = ref(false);
    const loadingMembers = ref(false);
    const loadingProspects = ref(false);
    const loadingInvitations = ref(false);
    const toastMessage = ref('');
    const toastType = ref<'success' | 'error' | 'info' | 'warning'>('success');
    const showToast = ref(false);
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const loadingPromoteGerant = ref<{[key: number]: boolean}>({});
    const loadingSendInvitation = ref<{[key: number]: boolean}>({});
    const loadingValidateProspect = ref<{[key: number]: boolean}>({});
    const loadingRejectProspect = ref<{[key: number]: boolean}>({});
    const activeTab = ref('information');
    const selectedMembers = ref<number[]>([]);
    const selectedProspects = ref<number[]>([]);
    const currentUser = ref<any>(null);

    // États collapsables des sections
    const isCollectiviteOpen = ref(true);
    const isWalletOpen = ref(true);
    const isMembresOpen = ref(true);
    const isProspectsOpen = ref(true);

    // Pagination pour les membres
    const membersPagination = reactive({
        pageNumber: 1,
        total: 0,
        per_page: 10,
        last_page: 1
    });

    // Pagination pour les prospects
    const prospectsPagination = reactive({
        pageNumber: 1,
        total: 0,
        per_page: 10,
        last_page: 1
    });

    const monthsFr = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

    function formatDateFrench(dateString: string | null): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return `${date.getDate()} ${monthsFr[date.getMonth()]} ${date.getFullYear()}`;
    }

    function formatAmountFr(amount: number | null | undefined): string {
        if (amount === null || amount === undefined) return '0 F';
        return new Intl.NumberFormat('fr-FR').format(amount) + ' F';
    }

    function calculatePercentage(value: number | null, total: number | null): number {
        if (!value || !total || total === 0) return 0;
        return Math.round((value / total) * 100 * 10) / 10;
    }

    async function fetchCollectiviteDetails() {
        if (!collectivite.value.id) return;
        try {
            loading.value = true;
            const response = await axios.get(`/backoffice/collectivites/get/${collectivite.value.id}`);
            collectivite.value = response.data;
        } catch (error: any) {
            console.error("Erreur lors du chargement de la collectivité:", error);
        } finally {
            loading.value = false;
        }
    }

    async function fetchUsersCount() {
        if (!collectivite.value.id) return;
        try {
            const response = await axios.get(`/backoffice/users/list?collectivite_id=${collectivite.value.id}&pageSize=1`);
            usersCount.value = response.data.total || 0;
        } catch (error: any) {
            console.error("Erreur lors du chargement du nombre de membres:", error);
        }
    }

    async function fetchMembers() {
        if (!collectivite.value.id) return;
        try {
            loadingMembers.value = true;
            const response = await axios.get(`/backoffice/users/list?collectivite_id=${collectivite.value.id}&pageSize=${membersPagination.per_page}&pageNumber=${membersPagination.pageNumber}&verified=1`);
            membersList.value = response.data.data || [];
            membersPagination.total = response.data.total || 0;
            membersPagination.last_page = response.data.last_page || 1;
        } catch (error: any) {
            console.error("Erreur lors du chargement des membres:", error);
        } finally {
            loadingMembers.value = false;
        }
    }

    async function fetchProspects() {
        if (!collectivite.value.id) return;
        try {
            loadingProspects.value = true;
            const response = await axios.get(`/backoffice/prospects/list?collectivite_id=${collectivite.value.id}&pageSize=${prospectsPagination.per_page}&pageNumber=${prospectsPagination.pageNumber}`);
            prospectsList.value = response.data.data || [];
            prospectsPagination.total = response.data.total || 0;
            prospectsPagination.last_page = response.data.last_page || 1;
        } catch (error: any) {
            console.error("Erreur lors du chargement des prospects:", error);
        } finally {
            loadingProspects.value = false;
        }
    }

    onMounted(async () => {
        fetchCollectiviteDetails();
        fetchUsersCount();
        fetchMembers();
        fetchProspects();

        // Récupérer les informations de l'utilisateur connecté
        try {
            const response = await axios.get('/backoffice/auth/user');
            currentUser.value = response.data;
        } catch (error) {
            console.error("Erreur lors de la récupération de l'utilisateur:", error);
        }
    });

    function onMembersImported() {
        refreshMembers.value++;
        fetchCollectiviteDetails();
        fetchUsersCount();
        fetchMembers();
        fetchProspects();
    }

    function onMembersPaginate(pageNumber: number) {
        membersPagination.pageNumber = pageNumber;
        fetchMembers();
    }

    function onProspectsPaginate(pageNumber: number) {
        prospectsPagination.pageNumber = pageNumber;
        fetchProspects();
    }

    async function downloadCsv() {
        try {
            const response = await axios.get("/backoffice/collectivites/membres/download_csv_template", { responseType: "blob" });
            const blob = new Blob([response.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "collectivite_membres_template.csv");
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Erreur de téléchargement :", error);
        }
    }

    async function sendInvitationsUnverified(memberId: number) {
        const confirmation = await confirmationModalRef.value?.open(
            "Confirmer l'envoi des invitations",
            `Voulez-vous vraiment envoyer une invitation au membre sélectionné ?`
        );
        if (confirmation) {
            loadingSendInvitation.value[memberId] = true;
            try {
                const response = await axios.post('/backoffice/collectivites/membres/send_invitations_unverified', {
                    users_ids: [memberId],
                    collectivite_id: collectivite.value.id
                });
                toastMessage.value = 'Invitation envoyée avec succès';
                toastType.value = 'success';
                showToast.value = true;
            } catch (error: any) {
                console.error("Erreur lors de l'envoi de l'invitation:", error);
                toastMessage.value = error.response?.data?.message || "Une erreur est survenue lors de l'envoi de l'invitation";
                toastType.value = 'error';
                showToast.value = true;
            } finally {
                loadingSendInvitation.value[memberId] = false;
            }
        }
    }

    async function promoteGerant(member: any) {
        if (!collectivite.value.id || !member.id) return;

        try {
            const confirmation = await confirmationModalRef.value?.open(
                "Promouvoir en gérant",
                `Voulez-vous vraiment promouvoir ${member.nom} ${member.prenom} en tant que gérant de cette collectivité ?`
            );

            if (!confirmation) {
                return;
            }

            loadingPromoteGerant.value[member.id] = true;

            const response = await axios.post('/backoffice/collectivites/membres/promote_gerant', {
                user_id: member.id,
                collectivite_id: collectivite.value.id
            });

            toastMessage.value = response.data.message || 'Membre promu gérant avec succès';
            toastType.value = 'success';
            showToast.value = true;

            // Rafraîchir la liste des membres
            setTimeout(() => {
                fetchMembers();
            }, 1000);
        } catch (error: any) {
            console.error("Erreur lors de la promotion du gérant:", error);
            toastMessage.value = error.response?.data?.message || "Une erreur est survenue lors de la promotion";
            toastType.value = 'error';
            showToast.value = true;
        } finally {
            loadingPromoteGerant.value[member.id] = false;
        }
    }

    async function sendInvitation(prospect: any) {
        try {
            loadingSendInvitation.value[prospect.id] = true;
            const response = await axios.post('/backoffice/users/send_confirmation_email', {
                user_id: prospect.id
            });
            showToast.value = true;
            toastMessage.value = 'Invitation envoyée avec succès';
            toastType.value = 'success';
            fetchProspects();
        } catch (error: any) {
            showToast.value = true;
            toastMessage.value = error.response?.data?.message || error.message || "Erreur lors de l'envoi de l'invitation";
            toastType.value = 'error';
        } finally {
            loadingSendInvitation.value[prospect.id] = false;
        }
    }

    function getStatusTags(item: any) {
        const tags = [];
        if (item.email_verified_at) {
            tags.push({ label: 'Authentification', type: 'success' });
        } else {
            tags.push({ label: 'Authentification', type: 'warning' });
        }

        if (item.souscription_id) {
            tags.push({ label: 'Souscription', type: 'success' });
        } else {
            tags.push({ label: 'Souscription', type: 'warning' });
        }

        if (item.code_securite) {
            tags.push({ label: 'Code de sécurité', type: 'success' });
        } else {
            tags.push({ label: 'Code de sécurité', type: 'warning' });
        }

        if (item.actif) {
            tags.push({ label: 'Connecter', type: 'success' });
        } else {
            tags.push({ label: 'Connecter', type: 'warning' });
        }

        return tags;
    }

    function goToEdit() {
        router.push(`/app/collectivites/form/${collectivite.value.id}`);
    }

    function goToOnboarding() {
        router.push(`/app/collectivites/onboarding-groupe/${collectivite.value.id}`);
    }

    function goToPrelevements() {
        router.push(`/app/collectivites/${collectivite.value.id}/prelevements`);
    }

    // Vérifier si l'utilisateur est gérant de cette collectivité
    const isGerant = computed(() => {
        if (!currentUser.value || !collectivite.value.id) return false;

        // Vérifier si l'utilisateur est gérant via la table pivot user_collectivites
        const userCollectivites = currentUser.value.collectivites || [];
        const collectiviteGeree = userCollectivites.find(
            (c: any) => c.id === collectivite.value.id && c.pivot?.is_gerant === true
        );

        return !!collectiviteGeree;
    });

    async function validateProspect(prospect: any) {
        if (!prospect.id) return;

        try {
            const confirmation = await confirmationModalRef.value?.open(
                "Valider le prospect",
                `Voulez-vous vraiment valider ${prospect.nom} ${prospect.prenom} ? Ce prospect deviendra un membre actif de la collectivité.`
            );

            if (!confirmation) {
                return;
            }

            loadingValidateProspect.value[prospect.id] = true;

            const response = await axios.post('/backoffice/prospects/validate', {
                user_id: prospect.id
            });

            toastMessage.value = response.data.message || 'Prospect validé avec succès';
            toastType.value = 'success';
            showToast.value = true;

            // Rafraîchir la liste des prospects et membres
            setTimeout(() => {
                fetchProspects();
                fetchMembers();
            }, 1000);
        } catch (error: any) {
            console.error("Erreur lors de la validation du prospect:", error);
            toastMessage.value = error.response?.data?.message || "Une erreur est survenue lors de la validation";
            toastType.value = 'error';
            showToast.value = true;
        } finally {
            loadingValidateProspect.value[prospect.id] = false;
        }
    }

    async function rejectProspect(prospect: any) {
        if (!prospect.id) return;

        try {
            const confirmation = await confirmationModalRef.value?.open(
                "Rejeter le prospect",
                `Voulez-vous vraiment rejeter ${prospect.nom} ${prospect.prenom} ? Ce prospect sera transféré vers la collectivité par défaut pour un éventuel recontact ultérieur.`
            );

            if (!confirmation) {
                return;
            }

            loadingRejectProspect.value[prospect.id] = true;

            const response = await axios.post('/backoffice/prospects/reject', {
                user_id: prospect.id
            });

            toastMessage.value = response.data.message || 'Prospect rejeté avec succès';
            toastType.value = 'success';
            showToast.value = true;

            // Rafraîchir la liste des prospects
            setTimeout(() => {
                fetchProspects();
            }, 1000);
        } catch (error: any) {
            console.error("Erreur lors du rejet du prospect:", error);
            toastMessage.value = error.response?.data?.message || "Une erreur est survenue lors du rejet";
            toastType.value = 'error';
            showToast.value = true;
        } finally {
            loadingRejectProspect.value[prospect.id] = false;
        }
    }

    // Computed pour les statistiques
    const stats = computed(() => collectivite.value.stats || {});

    const membersPercentage = computed(() => {
        const maxMembers = 50000; // Valeur par défaut pour la barre de progression
        return Math.min(100, calculatePercentage(stats.value.total_users_count, maxMembers));
    });

    const prelevementPercentage = computed(() => {
        const current = stats.value.current_prelevement;
        if (!current) return 0;
        return calculatePercentage(current.montant_prelevement, current.montant_prelevement_expected);
    });

    const decaissementPercentage = computed(() => {
        const current = stats.value.current_decaissement;
        if (!current) return 0;
        return calculatePercentage(current.montant_decaissement, current.montant_decaissement_expected);
    });

    const activeBalancePercentage = computed(() => {
        return calculatePercentage(stats.value.active_balance, stats.value.total_balance);
    });

    const blockedBalancePercentage = computed(() => {
        return calculatePercentage(stats.value.blocked_balance, stats.value.total_balance);
    });

    const walletSolde = computed(() => {
        const totalPrelevements = stats.value.total_prelevements || 0;
        const totalDecaissements = stats.value.total_decaissements || 0;
        return totalPrelevements - totalDecaissements;
    });

    const walletSoldePercentage = computed(() => {
        const totalPrelevements = stats.value.total_prelevements || 0;
        if (totalPrelevements === 0) return 0;
        return calculatePercentage(walletSolde.value, totalPrelevements);
    });

    const decaissementTotalPercentage = computed(() => {
        const totalPrelevements = stats.value.total_prelevements || 0;
        if (totalPrelevements === 0) return 0;
        return calculatePercentage(stats.value.total_decaissements, totalPrelevements);
    });

</script>
<template>
    <section class="container-full-1 bg-grey-50">
        <div class="page-space-40"></div>

        <h4 class="s7 font-semibold px-3 pb-5">Collectivité / détail <span class="text-sm text-grey-500"></span></h4>
        <div class="s13 flex-right gap-3 mt--2 mb-5 px-5">
            <span class="btn shadow-200 white2" :class="{ 'active': activeTab === 'information' }" @click="activeTab = 'information'">Information</span>
            <span class="btn shadow-200 white2" :class="{ 'active': activeTab === 'membres' }" @click="activeTab = 'membres'">Membres</span>
            <span class="btn shadow-200 light2 border-none" :class="{ 'active': activeTab === 'prospects' }" @click="activeTab = 'prospects'">Prospects</span>
        </div>

        <!--Collectivité - Component-->
        <div class="s20 widget px-6 pt-8 pb-1">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open" :class="{ 'active': isCollectiviteOpen }" @click="isCollectiviteOpen = !isCollectiviteOpen" style="cursor: pointer;">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Collectivité
                </span>
            </div>
            <div class="s10 mt--5-5 gap-4 flex-right">
                <button class="btn lg py-3 white2 active" @click.stop="goToEdit">
                    <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                    <span>Editer</span>
                </button>
                <button class="btn lg py-3 white2 active" @click.stop="goToOnboarding">
                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                    <span>Onboarding</span>
                </button>
                <button class="btn lg py-3 white2 active" @click.stop="goToPrelevements">
                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                    <span>Prélèvements / Décaissements</span>
                </button>
            </div>
            <div class="clear-both h-5"></div>

            <div class="s20 collapse-content pt-5 pb-8" :class="{ 'active': isCollectiviteOpen }" v-show="isCollectiviteOpen">
                <div class="s3_mobile px-8">
                    <div class="rounded-100 overflow-hidden item active">
                        <img :src="collectivite.logo ? (BACKEND_URL + '/assets/collectivites/' + collectivite.logo) : '/assets/logos/logo2.svg'" width="100%" />
                    </div>
                    <h6 class="font-normal mt-7 mb-4">
                        <b class="text-xl">{{ collectivite.pays?.nom || 'Côte d\'Ivoire' }} | {{ Array.isArray(collectivite.secteurs) ? collectivite.secteurs.join(', ') : (collectivite.secteurs || 'N/A') }}</b>
                    </h6>
                    <h6 class="font-normal mb-4">
                        {{ collectivite.telephone || '' }} {{ collectivite.email ? '| ' + collectivite.email : '' }}<br class="mb-2"/>
                    </h6>
                    <h6 class="font-normal mb-4">
                        Adresse <br class="mb-2"/>
                        <b class="text-xl">{{ collectivite.adresse || 'N/A' }}</b>
                    </h6>
                </div>
                <div class="s4_mobile px-2">
                    <h3 class="mb-2">{{ collectivite.nom || '' }}</h3>
                    <h4 class="mb-5">{{ collectivite.rccm || 'N/A' }}</h4>
                    <span class="tag-state notif stroke">Actif</span>

                    <h6 class="font-normal mt-5 mb-4" v-if="collectivite.date_souscription">
                        Date de souscription <br class="mb-2"/>
                        <b class="text-xl">{{ formatDateFrench(collectivite.date_souscription) }}</b>
                    </h6>

                    <h6 class="font-normal mt-5 mb-4" v-if="collectivite.date_creation || collectivite.created_at">
                        Date de création <br class="mb-2"/>
                        <b class="text-xl">{{ formatDateFrench(collectivite.date_creation || collectivite.created_at) }}</b>
                    </h6>

                    <h6 class="font-normal mb-4" v-if="collectivite.rccm">
                        Numero d'immatriculation <br class="mb-2"/>
                        <b class="text-xl">{{ collectivite.rccm }}</b>
                    </h6>
                    <h6 class="font-normal mb-4">
                        Adresse <br class="mb-2"/>
                        <b class="text-xl">{{ collectivite.adresse || 'N/A' }}</b>
                    </h6>
                    <hr v-if="collectivite.justificatif"/>
                    <h6 class="font-normal mb-4" v-if="collectivite.justificatif">
                        Justificatif <br class="mb-2"/>
                        <b class="text-xl">{{ collectivite.justificatif }}</b>
                    </h6>
                </div>
                <div class="s13_mobile px-5">
                    <!--Barre de progression - Membres-->
                    <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="widget-floating py-0 text-xl flex-right">Membres {{ stats.total_users_count ? Math.round((stats.total_users_count / 50000) * 100) : 0 }}%</div>
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 text-2xl px-5 bg-pop-green" :style="{ width: membersPercentage + '%' }">
                            <div class="text-2xl position-absolute">{{ stats.total_users_count || 0 }} / 50000</div>
                        </div>
                    </div>
                    <div class="clear-both h-15"></div>

                    <!--Prélèvement du mois courant-->
                    <h3 class="font-normal mb-3">Prélèvement de {{ stats.current_month_label || 'Janvier 2026' }}</h3>
                    <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="widget-floating py-0 text-xl flex-right">{{ prelevementPercentage }}%</div>
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 px-7 bg-pop-green" :style="{ width: prelevementPercentage + '%' }">
                            <h2 class="position-absolute">{{ formatAmountFr(stats.current_prelevement?.montant_prelevement) }} / {{ formatAmountFr(stats.current_prelevement?.montant_prelevement_expected || 1000000) }}</h2>
                        </div>
                    </div>
                    <div class="clear-both h-15"></div>

                    <!--Décaissement du mois courant-->
                    <h3 class="font-normal mb-3">Décaissement de {{ stats.current_month_label || 'Janvier 2026' }}</h3>
                    <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="widget-floating py-0 text-xl flex-right">{{ decaissementPercentage }}%</div>
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 px-7 bg-color-1000" :style="{ width: decaissementPercentage + '%' }">
                            <h2 class="position-absolute text-white">{{ formatAmountFr(stats.current_decaissement?.montant_decaissement) }} / {{ formatAmountFr(stats.current_decaissement?.montant_decaissement_expected || 1000000) }}</h2>
                        </div>
                    </div>
                </div>
                <div class="clear-both h-10"></div>
                <hr class="mb-8"/>

                <!--Statistiques financières-->
                <div class="s20 flex items-center px-8 pb-2 gap-6">
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-1000"></div>
                            Débloqué
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmountFr(stats.active_balance) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ activeBalancePercentage }}%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" :style="{ width: Math.max(activeBalancePercentage, 5) + '%' }">
                                {{ activeBalancePercentage }}%
                            </div>
                        </div>
                    </div>
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-purple"></div>
                            Bloqué
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmountFr(stats.blocked_balance) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ blockedBalancePercentage }}%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-purple text-white" :style="{ width: Math.max(blockedBalancePercentage, 5) + '%' }">
                                {{ blockedBalancePercentage }}%
                            </div>
                        </div>
                    </div>
                    <div class="s6">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-green-light"></div>
                            Total
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmountFr(stats.total_balance) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                                100%
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!--End Collectivité-->

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>


        <!--Wallet - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open" :class="{ 'active': isWalletOpen }" @click="isWalletOpen = !isWalletOpen" style="cursor: pointer;">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Wallet / Décaissement
                </span>
            </div>
            <div class="s10 mt--5-5 flex-right">
            </div>

            <div class="s20 gap-6 px-5 pb-8 collapse-content" :class="{ 'active': isWalletOpen }" v-show="isWalletOpen">
                <div class="clear-both h-10"></div>
                <div class="flex items-center gap-6">
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-black"></div>
                            Solde
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmountFr(walletSolde) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ walletSoldePercentage }}%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-black" :style="{ width: Math.max(walletSoldePercentage, 5) + '%' }">
                                {{ walletSoldePercentage }}%
                            </div>
                        </div>
                    </div>
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-1000"></div>
                            Montant Décaissé
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmountFr(stats.total_decaissements) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ decaissementTotalPercentage }}%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" :style="{ width: Math.max(decaissementTotalPercentage, 5) + '%' }">
                                {{ decaissementTotalPercentage }}%
                            </div>
                        </div>
                    </div>
                    <div class="s6">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-green-light"></div>
                            Dernier décaissement
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmountFr(stats.last_decaissement?.montant_decaissement) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                                100%
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
        <!--End Wallet -->
        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>


        <!--Membre - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open" :class="{ 'active': isMembresOpen }" @click="isMembresOpen = !isMembresOpen" style="cursor: pointer;">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Membres
                </span>
            </div>
         <!--    <div class="s10 mt--5-5 gap-4 flex-right">
                <button type="button" @click.stop="downloadCsv" class="btn lg py-3 white2 active">
                    <img src="/icons/icon-telecharger.svg" class="btn-icon" alt="" />
                    <span>Modèle CSV</span>
                </button>
                <label for="file-upload-membres-details" class="btn lg py-3 white2 active" style="cursor: pointer;" @click.stop>
                    <img src="/icons/icon-upload.svg" class="btn-icon" alt="" />
                    <span>Importer CSV</span>
                </label>
                <button type="button" @click.stop="sendInvitationsUnverified" class="btn lg py-3" :disabled="loadingInvitations">
                    <img src="/icons/icon-send.svg" class="btn-icon" alt="" />
                    <span v-if="!loadingInvitations">Envoyer invitations</span>
                    <span v-else>Envoi en cours...</span>
                </button>
            </div> -->
            <div class="clear-both h-5"></div>

            <div class="s20 items-center gap-6 px-0 pb-6 collapse-content" :class="{ 'active': isMembresOpen }" v-show="isMembresOpen">
                <!-- Import CSV Component -->
                <import-membres-component
                    v-if="collectivite.id"
                    :collectivite-id="collectivite.id"
                    :key="refreshMembers"
                    @completed="onMembersImported"
                />
                <div class="clear-both h-5"></div>
                <table class="table separator skin3 selection rounded">
                    <thead>
                        <tr>
                            <th><label class="btn-circle" for="check-members-all"><input type="checkbox" name="check-members-all" id="check-members-all" /></label></th>
                            <th></th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Information</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Utilisation</th>
                            <th class="flex-right">
                                <button class="btn-ultra color2">
                                    <div class="btn-show">
                                        <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                        <span>Actif</span>
                                    </div>
                                    <div class="btn-loading">
                                        <div class="loader-dots"></div>
                                    </div>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="loadingMembers">
                            <tr>
                                <td colspan="5" class="text-center text-grey-500 py-5">
                                    <div class="loader dark"></div>
                            </td>
                        </tr>
                        </template>
                        <template v-else-if="membersList.length === 0">
                            <tr>
                                <td colspan="5" class="text-center text-grey-500 py-5">
                                    Aucun membre trouvé
                            </td>
                        </tr>
                        </template>
                        <tr class="text-grey-700 over-effect" v-for="(member, index) in membersList" :key="'member_' + index">
                            <td class="selection-item">
                                <label class="btn-circle" :for="'member_check_' + index">
                                    <input type="checkbox" name="selectedMembers[]" :id="'member_check_' + index" :value="member.id" v-model="selectedMembers" />
                                </label>
                            </td>
                            <td class="flex">
                                <div class="square-xl rounded-15" style="background-color: rgb(var(--color-warning-rgb), 0.2);">
                                    <img :src="!!member.photo_url ? member.photo_url : '/assets/users_avatar/600w/user.jpg'" width="100%" />
                                </div>
                                <div class="square-xl rounded-15 ml-3 px-3 py-3" style="background-color: rgb(var(--color-warning-rgb), 0.2);" v-if="collectivite.logo">
                                    <img :src="BACKEND_URL + '/assets/collectivites/' + collectivite.logo" width="100%" />
                                </div>
                            </td>
                            <td style="width: 20%;">
                                <p class="text-xl font-semibold mb-2">
                                    {{ member.reference || 'N/A' }} &nbsp;
                                    <span class="tag-state warning stroke" v-if="!member.email_verified_at">En cours</span>
                                    <span class="tag-state notif stroke" v-if="member.actif">Actif</span>
                                </p>
                                {{ member.nom + ' ' + member.prenom }} - {{ member.collectivite?.nom || collectivite.nom }}
                            </td>
                            <td class="text-3xl">
                                {{ member.quotite_cessible_pourcent ? member.quotite_cessible_pourcent + '%' : '-' }}
                                <p class="mt-1">{{ member.quotite_cessible_montant ? formatAmountFr(member.quotite_cessible_montant) : '-' }}</p>
                            </td>
                            <td>
                                <div class="flex-right gap-4">
                                    <router-link :to="'/app/users/form/' + member.id">
                                    <button class="btn tips">
                                        <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                                    </button>
                                    </router-link>
                                    <button
                                        @click="promoteGerant(member)"
                                        class="btn-ultra color2 tips"
                                        :disabled="loadingPromoteGerant[member.id] || member.is_gerant"
                                        :title="member.is_gerant ? 'Déjà gérant' : 'Promouvoir en gérant'"
                                    >
                                        <div class="btn-show">
                                            <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                        </div>
                                        <div class="btn-loading" v-if="loadingPromoteGerant[member.id]">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                    <button class="btn white2 active" v-if="member.can_receive_invitation_link == true">
                                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                        <span>Envoyer lien d'invitation</span>
                                    </button>
                                    <button type="button" @click="sendInvitationsUnverified(member.id)" class="btn normal ml-3 justify-content-center btn-filtre" :disabled="loadingSendInvitation[member.id]" v-if="member.can_receive_invitation_link == true">
                                        <img src="/icons/icon-send.svg" class="btn-icon" alt="" />
                                        <span v-if="!loadingSendInvitation[member.id]">Envoyer invitation</span>
                                        <span v-else>Envoi...</span>
                                    </button>
                                    <router-link :to="'/app/users/' + member.id + '/details'">
                                        <button class="btn white2 active">
                                            <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Gérer</span>
                                        </button>
                                    </router-link>
                                    <router-link :to="'/app/users/' + member.id + '/carte'">
                                    <button class="btn white2 active">
                                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                        <span>Carte</span>
                                    </button>
                                    </router-link>
                                    <router-link :to="'/app/users/' + member.id + '/utilisation'">
                                    <button class="btn white2 active">
                                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                        <span>Utilisation</span>
                                    </button>
                                    </router-link>
                                    <router-link :to="'/app/users/' + member.id + '/plan'">
                                    <button class="btn white2 active">
                                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                        <span>Plan</span>
                                    </button>
                                    </router-link>
                                    <router-link :to="'/app/users/' + member.id + '/revolving'">
                                    <button class="btn white2 active">
                                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                        <span>Revolving</span>
                                    </button>
                                    </router-link>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <!--Pagination-->
                <div style="display: flex; flex-direction: row; justify-content: center;" v-if="membersList.length > 0">
                    <custom-pagination
                        v-model="membersPagination.pageNumber"
                        :total="membersPagination.total"
                        :per-page="membersPagination.per_page"
                        :last_page="membersPagination.last_page"
                        @update:modelValue="onMembersPaginate"
                    />
                </div>
            </div>
        </div>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>



        <!--Prospect - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open" :class="{ 'active': isProspectsOpen }" @click="isProspectsOpen = !isProspectsOpen" style="cursor: pointer;">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Prospects
                </span>
            </div>
            <div class="clear-both h-5"></div>

            <div class="s20 items-center gap-6 px-0 pb-6 collapse-content" :class="{ 'active': isProspectsOpen }" v-show="isProspectsOpen">
                <table class="table separator skin3 selection rounded">
                    <thead>
                        <tr>
                            <th><label class="btn-circle" for="check-prospects-all"><input type="checkbox" name="check-prospects-all" id="check-prospects-all" /></label></th>
                            <th></th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Information</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Utilisation</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Collectivité</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Statut</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Contact</th>
                            <th class="flex-right"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="loadingProspects">
                            <tr>
                                <td colspan="8" class="text-center text-grey-500 py-5">
                                    <div class="loader dark"></div>
                            </td>
                        </tr>
                        </template>
                        <template v-else-if="prospectsList.length === 0">
                            <tr>
                                <td colspan="8" class="text-center text-grey-500 py-5">
                                    Aucun prospect trouvé
                            </td>
                        </tr>
                        </template>
                        <tr class="text-grey-700 over-effect" v-for="(prospect, index) in prospectsList" :key="'prospect_' + index">
                            <td class="selection-item">
                                <label class="btn-circle" :for="'prospect_check_' + index">
                                    <input type="checkbox" name="selectedProspects[]" :id="'prospect_check_' + index" :value="prospect.id" v-model="selectedProspects" />
                                </label>
                            </td>
                            <td class="flex">
                                <div class="square-xl rounded-15 ml-3 px-3 py-3" style="background-color: rgb(var(--color-warning-rgb), 0.2);" v-if="collectivite.logo">
                                    <img :src="BACKEND_URL + '/assets/collectivites/' + collectivite.logo" width="100%" />
                                </div>
                            </td>
                            <td class="text-xl font-semibold" style="width: 15%;">
                                {{ prospect.nom }} {{ prospect.prenom }}
                            </td>
                            <td class="text-3xl">
                                {{ prospect.quotite_cessible_pourcent ? prospect.quotite_cessible_pourcent + '%' : '-' }}
                                <p class="mt-1">{{ prospect.quotite_cessible_montant ? formatAmountFr(prospect.quotite_cessible_montant) : '-' }}</p>
                            </td>
                            <td>
                                {{ prospect.collectivite?.nom || collectivite.nom }}
                            </td>
                            <td>
                                <p class="text-xl font-semibold mb-2">
                                    <span class="tag-state warning stroke">En attente</span>
                                </p>
                            </td>
                            <td>
                                <span class="text-xl font-semibold">{{ prospect.telephone || 'N/A' }}</span>
                                <br class="mb-2" />
                                {{ prospect.email || 'N/A' }}
                            </td>
                            <td style="width: 25%;">
                                <div class="flex-right gap-4">
                                    <router-link :to="'/app/users/form/' + prospect.id">
                                        <button class="btn tips">
                                            <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                                            <span>Editer</span>
                                        </button>
                                    </router-link>
                                    <!-- Bouton Rejeter - visible uniquement pour les gérants -->
                                    <button
                                        v-if="isGerant"
                                        class="btn-ultra danger w-100"
                                        @click="rejectProspect(prospect)"
                                        :disabled="loadingRejectProspect[prospect.id]"
                                    >
                                        <div class="btn-show">
                                            <img src="/icons/icon-delete.svg" class="btn-icon icon-white" alt="" />
                                            <span>Rejeter</span>
                                        </div>
                                        <div class="btn-loading" v-if="loadingRejectProspect[prospect.id]">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                    <!-- Bouton Valider - visible pour admin et gérant -->
                                    <button
                                        class="btn-ultra w-100"
                                        @click="validateProspect(prospect)"
                                        :disabled="loadingValidateProspect[prospect.id]"
                                    >
                                        <div class="btn-show">
                                            <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                            <span>Valider</span>
                                        </div>
                                        <div class="btn-loading" v-if="loadingValidateProspect[prospect.id]">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <!--Pagination-->
                <div style="display: flex; flex-direction: row; justify-content: center;" v-if="prospectsList.length > 0">
                    <custom-pagination
                        v-model="prospectsPagination.pageNumber"
                        :total="prospectsPagination.total"
                        :per-page="prospectsPagination.per_page"
                        :last_page="prospectsPagination.last_page"
                        @update:modelValue="onProspectsPaginate"
                    />
                </div>
            </div>
        </div>

        <div class="clear-both h-50"></div>

        <confirmation-modal ref="confirmationModalRef" />
        <toast-notification
            :message="toastMessage"
            :type="toastType"
            :show="showToast"
            @update:show="showToast = $event"
        />
    </section>
</template>
